import React, { useState } from 'react';
import { Box, Typography, TextField, IconButton } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';

// Suggested skill taxonomy for technical assistance workflows (Petrotec et al).
// Operators can also add custom skills via the free-text input.
const SUGGESTED_SKILLS = [
  { key: 'electrical',    pt: 'Eletricidade',      en: 'Electrical',       emoji: '⚡' },
  { key: 'mechanical',    pt: 'Mecânica',          en: 'Mechanical',       emoji: '🔧' },
  { key: 'lpg_pumps',     pt: 'Bombas GPL',        en: 'LPG pumps',        emoji: '🟢' },
  { key: 'fuel_pumps',    pt: 'Bombas combustível', en: 'Fuel pumps',      emoji: '⛽' },
  { key: 'electronics',   pt: 'Eletrónica',        en: 'Electronics',      emoji: '📟' },
  { key: 'welding',       pt: 'Soldadura',         en: 'Welding',          emoji: '🔥' },
  { key: 'adr',           pt: 'ADR (perigosas)',   en: 'ADR (hazardous)',  emoji: '☣️' },
  { key: 'refrigerated',  pt: 'Refrigerado',       en: 'Refrigerated',     emoji: '❄️' },
  { key: 'hydraulic',     pt: 'Hidráulica',        en: 'Hydraulic',        emoji: '💧' },
  { key: 'software',      pt: 'Software/Firmware', en: 'Software/Firmware',emoji: '💻' },
];

function labelFor(k, lang) {
  const s = SUGGESTED_SKILLS.find(x => x.key === k);
  if (s) return `${s.emoji} ${lang === 'pt' ? s.pt : s.en}`;
  return k;
}

/**
 * SkillsPicker — multi-select of skill tokens. Stores an array of keys
 * (from SUGGESTED_SKILLS) plus any free-text custom skills the operator added.
 *
 * @param {string[]} value        current skill keys
 * @param {function} onChange     called with new string[]
 * @param {string}   lang         'pt' | 'en'
 * @param {string}   emptyLabel   message when list is empty
 * @param {string}   accent       hex accent colour
 */
export default function SkillsPicker({ value = [], onChange, lang = 'pt', emptyLabel, accent = '#2563eb' }) {
  const [custom, setCustom] = useState('');

  const selected = Array.isArray(value) ? value : [];
  const toggle = (k) => {
    if (selected.includes(k)) onChange(selected.filter(x => x !== k));
    else onChange([...selected, k]);
  };
  const remove = (k) => onChange(selected.filter(x => x !== k));
  const addCustom = () => {
    const v = custom.trim();
    if (!v) return;
    if (selected.includes(v)) { setCustom(''); return; }
    onChange([...selected, v]);
    setCustom('');
  };

  return (
    <Box>
      {/* Currently selected — highlighted chips with "×" */}
      <Box style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6, minHeight: 22 }}>
        {selected.length === 0 && emptyLabel && (
          <Typography style={{ fontSize: '0.66rem', color: '#94a3b8', fontStyle: 'italic' }}>
            {emptyLabel}
          </Typography>
        )}
        {selected.map(k => (
          <Box key={k} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 6px 2px 10px', borderRadius: 12,
            background: accent, color: '#fff',
            fontSize: '0.68rem', fontWeight: 700,
          }}>
            {labelFor(k, lang)}
            <IconButton size="small" onClick={() => remove(k)}
              style={{ padding: 0, color: '#fff', marginLeft: 2 }}>
              <CloseIcon style={{ fontSize: 13 }} />
            </IconButton>
          </Box>
        ))}
      </Box>

      {/* Suggested pool */}
      <Box style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
        {SUGGESTED_SKILLS.map(s => {
          const active = selected.includes(s.key);
          if (active) return null; // already shown above
          return (
            <Box key={s.key}
              onClick={() => toggle(s.key)}
              style={{
                padding: '2px 8px', borderRadius: 12, cursor: 'pointer',
                fontSize: '0.66rem', fontWeight: 600,
                background: '#fff', color: '#475569',
                border: '1px solid #e2e8f0',
              }}>
              {s.emoji} {lang === 'pt' ? s.pt : s.en}
            </Box>
          );
        })}
      </Box>

      {/* Custom input */}
      <Box style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <TextField
          variant="outlined" size="small"
          placeholder={lang === 'pt' ? 'Adicionar competência personalizada…' : 'Add custom skill…'}
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
          inputProps={{ style: { padding: '5px 8px', fontSize: '0.7rem' } }}
          style={{ flex: 1 }}
        />
        <IconButton size="small" onClick={addCustom} style={{ padding: 4 }}>
          <AddIcon style={{ fontSize: 14, color: accent }} />
        </IconButton>
      </Box>
    </Box>
  );
}

export { SUGGESTED_SKILLS, labelFor };
